import { React, useState, useEffect, useCallback } from "react";
import AppWrapper from "../../AppWrapper";
import { useNavigate, useSearchParams } from "react-router-dom";
import supabase from "../../../supabase-client";
import {
  Autocomplete,
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  FormLayout,
  IndexTable,
  InlineGrid,
  InlineStack,
  Page,
  Select,
  Text,
  TextField,
  Toast,
} from "@shopify/polaris";
import { DeleteIcon, StarFilledIcon, StarIcon, PlusIcon } from "@shopify/polaris-icons";
import { stateList, bankList } from "../../../utility/GlobalVariables";
import { formatPrice } from "../../../utility/funcs";
import PopUpSearch from "../../CustomComponents/PopUpSearch/PopUpSearch";
import ClientCreditCard from "../../CustomComponents/ClientCredit/ClientCreditCard";
import PaymentClabe from "../../CustomComponents/PaymentClabe";
import ClientDocuments from "../../CustomComponents/ClientDocuments/ClientDocuments";
import ClientBenefits from "../../CustomComponents/ClientBenefits/ClientBenefits";
import useClientBlock from "../../CustomComponents/ClientBlock/useClientBlock";

// Hoisted so the read-only view and the edit form resolve codes through the
// same source. Duplicating these lists is how a page ends up showing "626"
// in one place and "RESICO" in another.
const REGIMEN_OPTIONS = [
  { label: "Seleccionar", value: "" },
  { label: "601 — General de Ley Personas Morales", value: "601" },
  { label: "603 — Personas Morales con Fines no Lucrativos", value: "603" },
  { label: "620 — Sociedades Cooperativas de Producción", value: "620" },
  { label: "622 — Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras", value: "622" },
  { label: "623 — Opcional para Grupos de Sociedades", value: "623" },
  { label: "624 — Coordinados", value: "624" },
  { label: "626 — Régimen Simplificado de Confianza (RESICO)", value: "626" },
];
const CFDI_USE_OPTIONS = [
  { label: "G01 — Adquisición de mercancías", value: "G01" },
  { label: "G03 — Gastos en general", value: "G03" },
  { label: "I03 — Equipo de transporte", value: "I03" },
  { label: "S01 — Sin efectos fiscales", value: "S01" },
];
const PAYMENT_TYPE_OPTIONS = [
  { label: "PUE — Pago en una sola exhibición", value: "PUE" },
  { label: "PPD — Pago en parcialidades o diferido", value: "PPD" },
];
const PAYMENT_FORM_OPTIONS = [
  { label: "01 — Efectivo", value: "cash" },
  { label: "03 — Transferencia electrónica", value: "transfer" },
  { label: "04 — Tarjeta de crédito", value: "credit-card" },
  { label: "28 — Tarjeta de débito", value: "debit-card" },
  { label: "99 — Por definir", value: "other" },
];

// Codes are meaningless to a human reading the page; show the label.
const labelFor = (options, value) =>
  options.find((o) => o.value === value)?.label || value || "—";


const API_URL = "https://api.gomoverent.com/v2/alegra/proxy.php";
const API_TOKEN = "h8ISmsSIwTvfl8Tny2nr0dFh2nz3rmiCzBMjtCJOJqn7TF6Y4wnch7uI1FI3ArlZP0wfMsGkEuqNXUzaX9wYSL5bnn9CzB1DkWAMFQVCBWBhRwAXn29M96YYxcp1bmfN";

const SAT_TO_ALEGRA_12 = {
  "601": "GENERAL_REGIME_OF_MORAL_PEOPLE_LAW",
  "603": "REGIME_OF_MORAL_PEOPLE_NOT_PROFIT",
  "620": "REGIME_OF_COOPERATIVE_PRODUCTION_SOCIETIES",
  "622": "PRIMARY_SECTOR_REGIME",
  "623": "SOCIETIES_OPTIONAL_REGIME",
  "624": "REGIME_OF_THE_COORDINATED",
  "626": "REGIME_OF_TRUST",
};

// Common puestos for company contacts. The field is "select or write": these
// are suggestions; any free-text value is allowed.
const ROLE_SUGGESTIONS = [
  "Facturación",
  "Dueño",
  "Administrador",
  "Operaciones",
  "Cobranza",
  "Chofer",
  "Contacto",
];

const normRole = (r) => (r || "").trim();

// Editable position field for a single contact row. Behaves as a combobox:
// pick a suggestion or type any value. Fully controlled — every change is
// reported up via onChange so the edit becomes part of the Guardar dirty state
// (persisted on save, not on blur).
function ContactRoleField({ value, onChange }) {
  const [options, setOptions] = useState(
    ROLE_SUGGESTIONS.map((r) => ({ value: r, label: r }))
  );
  const text = value || "";

  const updateText = useCallback(
    (v) => {
      onChange(v);
      const q = v.toLowerCase();
      setOptions(
        ROLE_SUGGESTIONS.filter((r) => r.toLowerCase().includes(q)).map((r) => ({
          value: r,
          label: r,
        }))
      );
    },
    [onChange]
  );

  const handleSelect = useCallback(
    (selected) => {
      onChange(selected[0] || "");
      setOptions(ROLE_SUGGESTIONS.map((r) => ({ value: r, label: r })));
    },
    [onChange]
  );

  return (
    <Autocomplete
      options={options}
      selected={[]}
      onSelect={handleSelect}
      textField={
        <Autocomplete.TextField
          label="Puesto"
          labelHidden
          value={text}
          onChange={updateText}
          placeholder="Puesto (ej. Facturación)"
          autoComplete="off"
        />
      }
    />
  );
}

function CompanyEdit() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const editingId = searchParams.get("id");
  const [isLoading, setIsLoading] = useState(false);
  // Snapshot of the last-persisted company fields, for dirty tracking. The
  // Guardar button is enabled only while `company` differs from this.
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  // { content, error } — Polaris Toast shown after a save attempt.
  const [toast, setToast] = useState(null);

  const [company, setCompany] = useState({
    name: "",
    rfc: "",
    regimen_fiscal: "",
    codigo_postal_fiscal: "",
    cfdi_use: "G03",
    payment_form: "transfer",
    payment_type_cfdi: "PUE",
    credit_days: null,
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    address: "",
    city: "",
    state: "",
    notes: "",
    bank_name: "",
    bank_clabe: "",
    bank_holder_name: "",
  });

  const stateOptions = [
    { value: "", label: "Seleccionar" },
    ...stateList.map((s) => ({ value: s, label: s })),
  ];

  // Default to "Seleccionar…" so an empty bank doesn't silently become the
  // first entry in the list. If a stored value isn't in our list (legacy /
  // free-text), keep it as an option so it displays and isn't lost.
  const bankOptions = [
    { value: "", label: "Seleccionar…" },
    ...(company.bank_name && !bankList.includes(company.bank_name)
      ? [{ value: company.bank_name, label: company.bank_name }]
      : []),
    ...bankList.map((b) => ({ value: b, label: b })),
  ];

  // CLABE must be exactly 18 digits when provided — block saving incomplete
  // bank details. Empty is allowed (no account on file).
  const clabeInvalid =
    !!company.bank_clabe && company.bank_clabe.length !== 18;

  const [contacts, setContacts] = useState([]);
  const [contactSearchOpen, setContactSearchOpen] = useState(false);

  const fetchContacts = async (companyId) => {
    if (!companyId) return;
    const { data } = await supabase
      .from("company_contacts")
      .select(
        "id, role, is_primary, customer:customers(id, first_name, last_name, email, phone)"
      )
      .eq("company_id", companyId)
      .order("is_primary", { ascending: false })
      .order("id");
    const fresh = data || [];
    // `_savedRole` is the persisted baseline per row; `role` is the working
    // value (part of the Guardar dirty state). Structural ops (add/remove/star)
    // refetch — preserve any unsaved role edits so they aren't lost here.
    setContacts((prev) => {
      const pending = new Map(
        prev
          .filter((c) => normRole(c.role) !== normRole(c._savedRole))
          .map((c) => [c.id, c.role])
      );
      return fresh.map((c) => ({
        ...c,
        _savedRole: c.role ?? null,
        role: pending.has(c.id) ? pending.get(c.id) : c.role ?? null,
      }));
    });
  };

  const syncPrimaryToCompany = async (companyId, primary) => {
    if (!companyId) return;
    const name = primary
      ? `${primary.customer.first_name || ""} ${primary.customer.last_name || ""}`.trim()
      : "";
    await supabase
      .from("companies")
      .update({
        contact_name: name,
        contact_phone: primary?.customer?.phone || "",
        contact_email: primary?.customer?.email || "",
      })
      .eq("id", companyId);
    const synced = {
      contact_name: name,
      contact_phone: primary?.customer?.phone || "",
      contact_email: primary?.customer?.email || "",
    };
    setCompany((prev) => ({ ...prev, ...synced }));
    // These three are persisted above, so fold them into the saved snapshot
    // too — otherwise a primary-contact change would falsely mark the form dirty.
    setSavedSnapshot((prev) => (prev ? { ...prev, ...synced } : prev));
  };

  const addContact = async ({ id }) => {
    if (!editingId) return;
    const alreadyLinked = contacts.some((c) => c.customer.id === id);
    if (alreadyLinked) {
      setContactSearchOpen(false);
      return;
    }
    const makePrimary = contacts.length === 0;
    const { error } = await supabase.from("company_contacts").insert({
      company_id: parseInt(editingId),
      customer_id: id,
      is_primary: makePrimary,
    });
    if (error) {
      alert("Error: " + error.message);
      return;
    }
    await fetchContacts(editingId);
    if (makePrimary) {
      const { data } = await supabase
        .from("company_contacts")
        .select(
          "id, is_primary, customer:customers(id, first_name, last_name, email, phone)"
        )
        .eq("company_id", editingId)
        .eq("is_primary", true)
        .single();
      if (data) await syncPrimaryToCompany(editingId, data);
    }
    setContactSearchOpen(false);
  };

  // Local-only edit; persisted by save() so it participates in the Guardar
  // dirty state like every other field.
  const setContactRole = (linkId, role) => {
    setContacts((prev) =>
      prev.map((c) => (c.id === linkId ? { ...c, role } : c))
    );
  };

  const removeContact = async (linkId, wasPrimary) => {
    await supabase.from("company_contacts").delete().eq("id", linkId);
    if (wasPrimary) {
      // Promote another contact to primary if any remain, else clear denormalized fields
      const { data: remaining } = await supabase
        .from("company_contacts")
        .select(
          "id, is_primary, customer:customers(id, first_name, last_name, email, phone)"
        )
        .eq("company_id", editingId)
        .order("id")
        .limit(1);
      if (remaining && remaining.length > 0) {
        await supabase
          .from("company_contacts")
          .update({ is_primary: true })
          .eq("id", remaining[0].id);
        await syncPrimaryToCompany(editingId, remaining[0]);
      } else {
        await syncPrimaryToCompany(editingId, null);
      }
    }
    await fetchContacts(editingId);
  };

  const setPrimary = async (linkId) => {
    // Clear all, then set one — index guarantees only one primary at a time
    await supabase
      .from("company_contacts")
      .update({ is_primary: false })
      .eq("company_id", editingId);
    await supabase
      .from("company_contacts")
      .update({ is_primary: true })
      .eq("id", linkId);
    const { data } = await supabase
      .from("company_contacts")
      .select(
        "id, is_primary, customer:customers(id, first_name, last_name, email, phone)"
      )
      .eq("id", linkId)
      .single();
    if (data) await syncPrimaryToCompany(editingId, data);
    await fetchContacts(editingId);
  };

  // Rental reservations billed to this company, newest first (same as the
  // person profile's history, plus who the reservation was for).
  const [reservationHistory, setReservationHistory] = useState([]);
  useEffect(() => {
    if (!editingId) return;
    (async () => {
      const { data } = await supabase
        .from("reservations")
        .select("id, date_range, total_price, status, fully_paid, car(plate, model(name)), customer(first_name, last_name)")
        .eq("company", editingId)
        .eq("type", "rental")
        .order("created_at", { ascending: false });
      if (data) setReservationHistory(data);
    })();
  }, [editingId]);

  useEffect(() => {
    if (!editingId) return;
    const fetchCompany = async () => {
      const { data, error } = await supabase
        .from("companies")
        .select("*")
        .eq("id", editingId)
        .single();
      if (error) {
        console.error(error.message);
        return;
      }
      const mapped = {
        name: data.name || "",
        rfc: data.rfc || "",
        regimen_fiscal: data.regimen_fiscal || "",
        codigo_postal_fiscal: data.codigo_postal_fiscal || "",
        cfdi_use: data.cfdi_use || "G03",
        payment_form: data.payment_form || "transfer",
        payment_type_cfdi: data.payment_type_cfdi || "PUE",
        credit_days: data.credit_days ?? null,
        contact_name: data.contact_name || "",
        contact_phone: data.contact_phone || "",
        contact_email: data.contact_email || "",
        address: data.address || "",
        city: data.city || "",
        state: data.state || "",
        notes: data.notes || "",
        bank_name: data.bank_name || "",
        bank_clabe: data.bank_clabe || "",
        bank_holder_name: data.bank_holder_name || "",
      };
      setCompany(mapped);
      setSavedSnapshot(mapped);
    };
    fetchCompany();
    fetchContacts(editingId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId]);

  const handleChange = (value, id) => {
    if (id === "rfc") value = value.toUpperCase();
    if (id === "codigo_postal_fiscal") value = value.replace(/\D/g, "").substring(0, 5);
    if (id === "bank_clabe") value = value.replace(/\D/g, "").substring(0, 18);
    if (id === "credit_days") {
      const digits = value.replace(/\D/g, "");
      value = digits === "" ? null : parseInt(digits, 10);
    }
    if (id === "contact_name") {
      value = value
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    }
    // Switching Método de Pago carries two coupled SAT rules:
    //  - Días de crédito only applies to PPD: default a fresh PPD company to a
    //    15-day term (keep any value already set), clear it for PUE.
    //  - PPD invoices must use Forma de Pago "99 — Por definir", so force it.
    if (id === "payment_type_cfdi") {
      if (value === "PPD") {
        setCompany({
          ...company,
          payment_type_cfdi: value,
          credit_days: company.credit_days == null ? 15 : company.credit_days,
          payment_form: "other",
        });
      } else {
        setCompany({ ...company, payment_type_cfdi: value, credit_days: null });
      }
      return;
    }
    setCompany({ ...company, [id]: value });
  };


  // Read-only view.
  //
  // Label ABOVE value, not label-left/value-right: in a wide card the two get
  // flung to opposite edges with a river of whitespace between them, and the
  // eye stops being able to pair them. Stacked keeps them adjacent regardless
  // of card width.
  const infoRow = (label, value) => (
    <BlockStack gap="050" key={label}>
      <Text tone="subdued" variant="bodySm">{label}</Text>
      <Text breakWord>{value || "—"}</Text>
    </BlockStack>
  );

  // Two columns of facts inside each card, and two columns of cards on wide
  // screens — twelve facts should not need a page and a half of scrolling.
  const infoCard = (title, rows) => (
    <Card>
      <BlockStack gap={300}>
        <Text variant="headingSm" as="h3">{title}</Text>
        <InlineGrid columns={{ xs: 1, sm: 2 }} gap={300}>
          {rows}
        </InlineGrid>
      </BlockStack>
    </Card>
  );

  const companyReadView = () => (
    <BlockStack gap={400}>
      <InlineGrid columns={{ xs: 1, md: ["twoThirds", "oneThird"] }} gap={400}>
        {infoCard("Generales", [
          infoRow("Nombre", company.name),
          infoRow("RFC", company.rfc),
        ])}
        {editingId ? (
          <PaymentClabe
            ownerType="company"
            ownerId={parseInt(editingId)}
            ownerName={company.name}
          />
        ) : null}
      </InlineGrid>

      {infoCard("Facturación", [
        infoRow("Régimen fiscal", labelFor(REGIMEN_OPTIONS, company.regimen_fiscal)),
        infoRow("Uso de CFDI", labelFor(CFDI_USE_OPTIONS, company.cfdi_use)),
        infoRow("Método de pago", labelFor(PAYMENT_TYPE_OPTIONS, company.payment_type_cfdi)),
        infoRow("Forma de pago", labelFor(PAYMENT_FORM_OPTIONS, company.payment_form)),
        infoRow("C.P. fiscal", company.codigo_postal_fiscal),
        ...(company.payment_type_cfdi === "PPD"
          ? [infoRow("Días de crédito", company.credit_days != null ? `${company.credit_days} días` : null)]
          : []),
      ])}

      <InlineGrid columns={{ xs: 1, md: 2 }} gap={400}>
        {infoCard("Contacto", [
          infoRow("Nombre", company.contact_name),
          infoRow("Teléfono", company.contact_phone),
          infoRow("Correo", company.contact_email),
        ])}

        {infoCard("Dirección", [
          infoRow("Calle", company.address),
          infoRow("Ciudad", company.city),
          infoRow("Estado", company.state),
        ])}
      </InlineGrid>

      {infoCard("Cuenta de la empresa (para devoluciones)", [
        infoRow("Banco", company.bank_name),
        infoRow("CLABE", company.bank_clabe),
        infoRow("Titular", company.bank_holder_name),
      ])}

      {/* Notas last and only when there are any — an empty box of nothing is
          noise on a page whose problem is already density. */}
      {company.notes ? infoCard("Notas", [infoRow("", company.notes)]) : null}
    </BlockStack>
  );

  // The asterisks mark what SAT requires on a CFDI receptor. They do NOT block
  // saving: 20 of 224 companies already have no RFC, and hard-blocking would
  // make those records uneditable — you could not fix an address without first
  // hunting down a tax ID. So the requirement is stated, what is missing is
  // named, and the save still goes through.
  const missingForInvoicing = [
    !company.rfc?.trim() && "RFC",
    !company.regimen_fiscal?.trim() && "Régimen fiscal",
    !company.codigo_postal_fiscal?.trim() && "Código postal fiscal",
  ].filter(Boolean);

  const save = async () => {
    setIsLoading(true);
    setToast(null);
    // Track the id we're saving against so a brand-new company can keep editing
    // (and its Alegra contact id can be persisted) without leaving the page.
    let savedId = editingId;
    // Holds an Alegra-sync failure message, if any, so we can warn instead of
    // silently letting the DB and Alegra contact drift apart.
    let alegraSyncWarning = null;
    if (editingId) {
      // paid_by_company_id only changes through set_company_payer() (shared
      // CLABE). Writing back the value loaded with the page would undo a change
      // made since, and the guard trigger would refuse the whole save.
      const { paid_by_company_id, ...fields } = company;
      const { error } = await supabase
        .from("companies")
        .update(fields)
        .eq("id", editingId);
      if (error) {
        setToast({ content: "Error al guardar: " + error.message, error: true });
        setIsLoading(false);
        return;
      }
    } else {
      const { data: inserted, error } = await supabase
        .from("companies")
        .insert((({ paid_by_company_id, ...fields }) => fields)(company))
        .select("id")
        .single();
      if (error) {
        setToast({ content: "Error al guardar: " + error.message, error: true });
        setIsLoading(false);
        return;
      }
      savedId = inserted?.id ? String(inserted.id) : null;
    }

    // Sync billing data to Alegra if RFC and regime are set
    if (company.rfc && company.regimen_fiscal) {
      try {
        const alegraRegime = SAT_TO_ALEGRA_12[company.regimen_fiscal] || "SIMPLIFIED_REGIME";
        // Check if contact exists
        const searchRes = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${API_TOKEN}` },
          body: JSON.stringify({ endpoint: `contacts?identification=${company.rfc}`, method: "GET" }),
        });
        const searchData = await searchRes.json();
        const existing = searchData.success && Array.isArray(searchData.data) && searchData.data.length > 0 ? searchData.data[0] : null;

        const contactBody = {
          name: company.name.toUpperCase(),
          identification: company.rfc,
          // Alegra requires the `regime` string (not just regimeObject) on
          // invoicing-enabled accounts, else the write is rejected with 2141
          // "El régimen es obligatorio". Send both, kept in sync.
          regime: alegraRegime,
          regimeObject: [alegraRegime],
          address: { zipCode: company.codigo_postal_fiscal || "", country: "MEX" },
        };

        if (existing) {
          // Update
          const putRes = await fetch(API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${API_TOKEN}` },
            body: JSON.stringify({ endpoint: `contacts/${existing.id}`, method: "PUT", body: contactBody }),
          });
          const putData = await putRes.json();
          if (!putData.success) {
            throw new Error(putData.error?.message || putData.error || "No se pudo actualizar el contacto en Alegra");
          }
          // Save alegra_contact_id if not set
          if (savedId && !company.alegra_contact_id) {
            await supabase.from("companies").update({ alegra_contact_id: String(existing.id) }).eq("id", savedId);
          }
        } else {
          // Create
          contactBody.type = ["client"];
          contactBody.thirdType = "NATIONAL";
          const createRes = await fetch(API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${API_TOKEN}` },
            body: JSON.stringify({ endpoint: "contacts", method: "POST", body: contactBody }),
          });
          const createData = await createRes.json();
          if (!createData.success) {
            throw new Error(createData.error?.message || createData.error || "No se pudo crear el contacto en Alegra");
          }
          if (createData.data?.id && savedId) {
            await supabase.from("companies").update({ alegra_contact_id: String(createData.data.id) }).eq("id", savedId);
          }
        }
      } catch (e) {
        // The company record itself saved fine; only the Alegra sync failed.
        // Surface it so the fiscal contact doesn't silently drift out of sync
        // (which surfaces as "el nombre es incorrecto" at stamping time).
        alegraSyncWarning = e?.message || String(e);
      }
    }

    // Persist any pending contact-role edits, then rebaseline them so the form
    // is clean afterward.
    const changedRoles = contacts.filter(
      (c) => normRole(c.role) !== normRole(c._savedRole)
    );
    for (const c of changedRoles) {
      const value = normRole(c.role) || null;
      const { error } = await supabase
        .from("company_contacts")
        .update({ role: value })
        .eq("id", c.id);
      if (error) {
        setToast({ content: "Error al guardar el puesto: " + error.message, error: true });
        setIsLoading(false);
        return;
      }
    }
    if (changedRoles.length > 0) {
      setContacts((prev) =>
        prev.map((c) => ({ ...c, _savedRole: normRole(c.role) || null }))
      );
    }

    // Stay on the page: mark the form clean and confirm with a toast. For a
    // brand-new company, switch the URL into edit mode so further saves update
    // the row we just created instead of inserting again.
    setSavedSnapshot(company);
    // Back to reading once the save lands: the page is for looking at far more
    // often than for editing.
    setIsEditing(false);
    if (!editingId && savedId) {
      setSearchParams({ id: savedId }, { replace: true });
    }
    setToast(
      alegraSyncWarning
        ? {
            content:
              "Empresa guardada, pero falló la sincronización fiscal en Alegra: " +
              alegraSyncWarning +
              ". Corrige y vuelve a guardar antes de facturar.",
            error: true,
          }
        : { content: "Cambios guardados", error: false }
    );
    setIsLoading(false);
  };

  // Dirty tracking: an existing company is dirty when it differs from the last
  // saved snapshot; a brand-new one is "dirty" until first saved (the empty-name
  // guard keeps it disabled until there's something worth saving). Contact role
  // edits count too — any row whose role drifted from its saved baseline.
  const companyDirty = savedSnapshot
    ? JSON.stringify(company) !== JSON.stringify(savedSnapshot)
    : true;
  const rolesDirty = contacts.some(
    (c) => normRole(c.role) !== normRole(c._savedRole)
  );
  const isDirty = companyDirty || rolesDirty;
  const [isEditing, setIsEditing] = useState(!editingId);

  // Discard edits and go back to reading. savedSnapshot is the persisted
  // baseline the dirty check already relies on, so there is no second source
  // of truth to keep in sync.
  const cancelEditing = () => {
    if (savedSnapshot) setCompany(savedSnapshot);
    setIsEditing(false);
  };

  // `company` state is an explicit field whitelist that excludes the block
  // columns, so Guardar can't clobber them — no onChange needed here.
  const clientBlock = useClientBlock({
    entityType: "company",
    entityId: editingId ? parseInt(editingId) : null,
    name: company.name,
  });

  return (
    <AppWrapper>
      {toast && (
        <Toast
          content={toast.content}
          error={toast.error}
          onDismiss={() => setToast(null)}
        />
      )}
      <Page
        title={!editingId ? "Crear Empresa" : company.name || "Empresa"}
        backAction={{ url: "/companies", content: "Empresas" }}
        actionGroups={clientBlock.actionGroups}
        primaryAction={
          // wrap=false: the Page header is narrow enough that Cancelar and
          // Guardar otherwise stack vertically on top of each other.
          isEditing ? (
            <InlineStack gap={200} wrap={false} blockAlign="center">
              {editingId && (
                <Button onClick={cancelEditing} disabled={isLoading}>
                  Cancelar
                </Button>
              )}
              <Button
              variant="primary"
              onClick={save}
              loading={isLoading}
              disabled={
                !company.name.trim() ||
                !isDirty ||
                isLoading ||
                clabeInvalid ||
                clientBlock.readOnly
              }
              >
                Guardar
              </Button>
            </InlineStack>
          ) : (
            <Button
              variant="primary"
              onClick={() => setIsEditing(true)}
              disabled={clientBlock.readOnly}
            >
              Editar
            </Button>
          )
        }
      >
        {clientBlock.markup}
        {/* A native disabled fieldset switches off every nested control at once,
            so a blocked company is read-only without touching each field. */}
        <fieldset
          disabled={clientBlock.readOnly}
          style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
        >
        {editingId && (
          <ClientCreditCard entityType="company" entityId={parseInt(editingId)} />
        )}
        {isEditing && missingForInvoicing.length > 0 && (
          <div style={{ marginBottom: "1rem" }}>
            <Banner tone="warning" title="Faltan datos para poder facturar">
              <p>
                {missingForInvoicing.join(", ")}. Puedes guardar sin ellos, pero
                no se podrá timbrar un CFDI a esta empresa.
              </p>
            </Banner>
          </div>
        )}
        {isEditing ? (
        <FormLayout>
          <FormLayout.Group condensed>
            <TextField
              label="Nombre de la Empresa"
              id="name"
              value={company.name}
              onChange={handleChange}
              requiredIndicator
            />
            <TextField
              label="RFC"
              id="rfc"
              requiredIndicator
              value={company.rfc}
              onChange={handleChange}
            />
          </FormLayout.Group>
          <FormLayout.Group title="Facturación" condensed>
            <Select
              label="Régimen Fiscal"
              id="regimen_fiscal"
              requiredIndicator
              value={company.regimen_fiscal}
              options={REGIMEN_OPTIONS}
              onChange={handleChange}
            />
            <TextField
              label="Código Postal Fiscal"
              id="codigo_postal_fiscal"
              requiredIndicator
              value={company.codigo_postal_fiscal}
              onChange={handleChange}
              maxLength={5}
            />
          </FormLayout.Group>
          <FormLayout.Group condensed>
            <Select
              label="Uso de CFDI"
              // placeholder renders a blank option when the stored value is
              // empty. Without it the browser shows the FIRST option as if it
              // were selected while state holds nothing — the field looks set,
              // is not, and (when required) Guardar stays disabled until you
              // change the value and change it back.
              id="cfdi_use"
              placeholder="Seleccionar"
              value={company.cfdi_use || ""}
              options={CFDI_USE_OPTIONS}
              onChange={handleChange}
            />
            <Select
              label="Método de Pago"
              id="payment_type_cfdi"
              placeholder="Seleccionar"
              value={company.payment_type_cfdi || ""}
              options={PAYMENT_TYPE_OPTIONS}
              onChange={handleChange}
            />
            {company.payment_type_cfdi === "PPD" && (
              <TextField
                label="Días de crédito"
                id="credit_days"
                type="number"
                min={0}
                suffix="días"
                value={company.credit_days == null ? "" : String(company.credit_days)}
                onChange={handleChange}
                autoComplete="off"
              />
            )}
            <Select
              label="Forma de Pago"
              id="payment_form"
              placeholder="Seleccionar"
              value={company.payment_form || ""}
              options={PAYMENT_FORM_OPTIONS}
              onChange={handleChange}
              // SAT rule: PPD invoices must use Forma de Pago "99 — Por definir".
              disabled={company.payment_type_cfdi === "PPD"}
              helpText={
                company.payment_type_cfdi === "PPD"
                  ? "PPD sólo admite 99 — Por definir."
                  : undefined
              }
            />
          </FormLayout.Group>
          <FormLayout.Group
            title="Datos de contacto"
            helpText="Se actualizan automáticamente con el contacto principal, pero puedes editarlos aquí."
          >
            <TextField
              label="Nombre de contacto"
              id="contact_name"
              value={company.contact_name}
              onChange={handleChange}
            />
            <TextField
              label="Teléfono"
              id="contact_phone"
              type="tel"
              value={company.contact_phone}
              onChange={handleChange}
            />
            <TextField
              label="Correo electrónico"
              id="contact_email"
              type="email"
              value={company.contact_email}
              onChange={handleChange}
            />
          </FormLayout.Group>
          <FormLayout.Group title="Dirección" condensed>
            <TextField
              label="Dirección"
              id="address"
              value={company.address}
              onChange={handleChange}
            />
            <TextField
              label="Ciudad"
              id="city"
              value={company.city}
              onChange={handleChange}
            />
            <Select
              label="Estado"
              id="state"
              value={company.state}
              options={stateOptions}
              onChange={handleChange}
            />
          </FormLayout.Group>
          <FormLayout.Group title="Datos Bancarios" condensed>
            <Select
              label="Banco"
              id="bank_name"
              value={company.bank_name || ""}
              options={bankOptions}
              onChange={handleChange}
            />
            <TextField
              label="CLABE de la empresa"
              id="bank_clabe"
              value={company.bank_clabe}
              onChange={handleChange}
              maxLength={18}
              placeholder="18 dígitos"
              error={clabeInvalid ? "La CLABE debe tener 18 dígitos" : undefined}
              helpText={
                company.bank_clabe && company.bank_clabe.length !== 18
                  ? `${company.bank_clabe.length} de 18 dígitos`
                  : "Su cuenta, para devoluciones."
              }
            />
            <TextField
              label="Titular de la cuenta"
              id="bank_holder_name"
              value={company.bank_holder_name}
              onChange={handleChange}
              placeholder={company.name || "Nombre de la empresa"}
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <TextField
              label="Notas"
              id="notes"
              value={company.notes}
              onChange={handleChange}
              multiline
            />
          </FormLayout.Group>
        </FormLayout>
        ) : (
          companyReadView()
        )}

        {/* Contacts render in both modes: knowing who to call is exactly
            what someone opens this page for. */}
          {editingId && (
            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <Text variant="headingSm" as="h3">
                    Contactos
                  </Text>
                  <Button
                    icon={PlusIcon}
                    onClick={() => setContactSearchOpen(true)}
                    size="slim"
                  >
                    Agregar contacto
                  </Button>
                </InlineStack>
                {contacts.length === 0 ? (
                  <Text tone="subdued">Sin contactos vinculados.</Text>
                ) : (
                  <BlockStack gap="200">
                    {contacts.map((c) => {
                      const fullName = `${c.customer?.first_name || ""} ${c.customer?.last_name || ""}`.trim();
                      return (
                        <InlineStack key={c.id} align="space-between" blockAlign="center" gap="200">
                          <BlockStack gap="050">
                            <InlineStack gap="200" blockAlign="center">
                              <Text
                                variant="bodyMd"
                                fontWeight="semibold"
                                onClick={() => navigate(`/customers/edit?id=${c.customer?.id}`)}
                                style={{ cursor: "pointer" }}
                              >
                                {fullName || "(sin nombre)"}
                              </Text>
                              {c.is_primary && <Badge tone="success">Principal</Badge>}
                            </InlineStack>
                            <Text variant="bodySm" tone="subdued">
                              {c.customer?.email || "—"}
                              {c.customer?.phone ? ` · ${c.customer.phone}` : ""}
                            </Text>
                          </BlockStack>
                          <InlineStack gap="100" blockAlign="center">
                            <div style={{ width: 200 }}>
                              <ContactRoleField
                                value={c.role}
                                onChange={(v) => setContactRole(c.id, v)}
                              />
                            </div>
                            <Button
                              icon={c.is_primary ? StarFilledIcon : StarIcon}
                              variant="plain"
                              onClick={() => !c.is_primary && setPrimary(c.id)}
                              disabled={c.is_primary}
                              accessibilityLabel="Marcar como principal"
                            />
                            <Button
                              icon={DeleteIcon}
                              variant="plain"
                              tone="critical"
                              onClick={() => removeContact(c.id, c.is_primary)}
                              accessibilityLabel="Quitar contacto"
                            />
                          </InlineStack>
                        </InlineStack>
                      );
                    })}
                  </BlockStack>
                )}
              </BlockStack>
            </Card>
          )}
        <div style={{ marginTop: "2rem" }}>
          <ClientDocuments
            entityType="company"
            entityId={editingId ? parseInt(editingId) : null}
          />
        </div>
        <div style={{ marginTop: "2rem" }}>
          <ClientBenefits
            entityType="company"
            entityId={editingId ? parseInt(editingId) : null}
          />
        </div>
        {editingId && reservationHistory.length > 0 && (
          <div style={{ marginTop: "2rem" }}>
            <Card>
              <BlockStack gap="300">
                <Text variant="headingMd">
                  Historial de Reservaciones ({reservationHistory.length})
                </Text>
                <IndexTable
                  resourceName={{ singular: "Reserva", plural: "Reservas" }}
                  selectable={false}
                  itemCount={reservationHistory.length}
                  headings={[
                    { title: "#" },
                    { title: "Cliente" },
                    { title: "Auto" },
                    { title: "Total" },
                    { title: "Estado" },
                  ]}
                >
                  {reservationHistory.map((res, index) => (
                    <IndexTable.Row
                      id={res.id}
                      key={res.id}
                      position={index}
                      onClick={() => navigate(`/reservation?id=${res.id}`)}
                    >
                      <IndexTable.Cell>
                        <Text fontWeight="semibold">#{res.id}</Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {[res.customer?.first_name, res.customer?.last_name].filter(Boolean).join(" ")}
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {res.car ? `${res.car.model?.name || ""} (${res.car.plate || ""})` : "—"}
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {formatPrice(res.total_price)}
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {res.status === "cancelled" ? (
                          <Badge tone="critical">Cancelada</Badge>
                        ) : res.status === "completed" ? (
                          <Badge>Completada</Badge>
                        ) : res.fully_paid ? (
                          <Badge tone="success">Pagada</Badge>
                        ) : (
                          <Badge tone="attention">Pendiente</Badge>
                        )}
                      </IndexTable.Cell>
                    </IndexTable.Row>
                  ))}
                </IndexTable>
              </BlockStack>
            </Card>
          </div>
        )}
        <PopUpSearch
          activated={contactSearchOpen}
          setOpen={setContactSearchOpen}
          getCustomer={({ id }) => addContact({ id })}
          title="Agregar Contacto"
          dbFunction="search_customers"
        />
        </fieldset>
      </Page>
    </AppWrapper>
  );
}

export default CompanyEdit;
